import { bridge } from "@multiweb/reactCore/bridge";
import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { IMenuItemData, MenuItemType } from "@multiweb/types/contracts";
import { Forms } from "../types";
import { SearchData, SearchGroup, SearchResult, SearchResultType, SubtreeIds } from "./types";

const { removeAccents } = bridge;

type StringCompareFn = (firstString: string, secondString: string) => number;

/**
 * Vytvoří funkci pro prioritní řazení výsledků vyhledávání. Přesná shoda s vyhledávaným textem se řadí jako první. Poté následují položky začínající vyhledávaným textem.
 * Nakonec zbytek.
 *
 * @param upperCaseAccentsFreeSearchedText Vyhledávaný text převedený na velká písmena a s odstraněnou diakritikou.
 * @param textCompareFn Funkce pro porovnání textu.
 */
export const createCompareSearchResults = (upperCaseAccentsFreeSearchedText: string, textCompareFn: StringCompareFn) => (firstResult: SearchResult, secondResult: SearchResult) => {
    const firstText = firstResult.text;
    const secondText = secondResult.text;
    const firstTextExactMatch = firstText === upperCaseAccentsFreeSearchedText;
    const secondTextExactMatch = secondText === upperCaseAccentsFreeSearchedText;
    const firstTextStartsWith = firstText.startsWith(upperCaseAccentsFreeSearchedText);
    const secondTextStartsWith = secondText.startsWith(upperCaseAccentsFreeSearchedText);

    if (firstTextExactMatch && secondTextExactMatch) {
        return 0;
    } else if (firstTextExactMatch) {
        return -1;
    } else if (secondTextExactMatch) {
        return 1;
    } else if (firstTextStartsWith && secondTextStartsWith) {
        return textCompareFn(firstText, secondText);
    } else if (firstTextStartsWith) {
        return -1;
    } else if (secondTextStartsWith) {
        return 1;
    } else {
        return textCompareFn(firstText, secondText);
    }
};

/**
 * Vytvoří data pro vyhledávání pro položku se zadaným textem a kódem formuláře.
 * @param id Id položky.
 * @param text Zobrazovaný název položky.
 * @param formCode Kód formuláře položky.
 * @param sourceType Typ zdroje položky. Využívá se pro mapování vyhledaných položek na SearchResult.
 */
export const createSearchData = (id: string | number, text: string | undefined, formCode: string | undefined, sourceType: SearchResultType): SearchData => ({
    id,
    text: text ?? "",
    upperCaseAccentsFreeText: text ? removeAccents(text.toLocaleUpperCase()) : "",
    upperCaseAccentsFreeFormCode: formCode ? removeAccents(formCode.toLocaleUpperCase()) : "",
    sourceType,
});

/**
 * Vytvoří funkci pro filtrování dat pro vyhledávání pomocí zadaného textu. Data splňují podmínku filtrování, pokud název nebo kód formuláře obsahuje hledaný text.
 * @param upperCaseAccentsFreeSearchedText Vyhledávaný text bez diakritiky převedený na velká písmena.
 */
export const createSearchDataFilter = (upperCaseAccentsFreeSearchedText: string) => (searchData: SearchData | undefined): boolean => {
    if (searchData) {
        const { upperCaseAccentsFreeText, upperCaseAccentsFreeFormCode } = searchData;
        return upperCaseAccentsFreeText.includes(upperCaseAccentsFreeSearchedText) ||
        upperCaseAccentsFreeFormCode.includes(upperCaseAccentsFreeSearchedText);
    } else {
        return false;
    }
};

/**
 * Vytvoří mapper pro převod SearchData na SearchResult pro zadanou skupinu vyhledávání.
 * @param searchGroup Skupina vyhledávání použitá pro sestavení unikátního klíče při vykreslování výsledků.
 */
export const createSearchDataToResultMapper = (searchGroup: SearchGroup) => ({ id, upperCaseAccentsFreeText, sourceType }: SearchData): SearchResult => ({
    id,
    text: upperCaseAccentsFreeText,
    type: sourceType,
    searchGroup,
});

/**
 * Vytvoří výsledek vyhledávání pro nadpis skupiny vyhledávání.
 * @param locKey Lokalizační klíč skupiny.
 */
export const createGroupHeaderResult = (locKey: SearchGroup): SearchResult => ({
    id: locKey,
    text: "",
    type: SearchResultType.groupHeader,
    searchGroup: locKey,
});

/**
 * Vytvoří konečný výsledek vyhledávání pro zadanou skupinu. Pokud skupina neobsahuje žádné výsledky, vrací prázdné pole. V opačném případě výsledky seřadí
 * podle zobrazovaného textu a jako první záznam poté přidá nadpis skupiny. Řazení probíhá na zadaném poli, neprovádí se jeho kopie.
 * @param groupLocKey Lokalizační klíč skupiny vyhledávání.
 * @param searchGroupResult Vyhledané položky ve skupině.
 * @param upperCaseAccentsFreeSearchedText Vyhledávaný text. Používá se pro prioritní řazení.
 * @param textCompareFn Funkce pro porovnání textu.
 */
export const sortAndFinalizeSearchGroup = (groupLocKey: SearchGroup, searchGroupResult: SearchResult[], upperCaseAccentsFreeSearchedText: string, textCompareFn: StringCompareFn): readonly SearchResult[] => {
    if (searchGroupResult.length > 0) {
        searchGroupResult.sort(createCompareSearchResults(upperCaseAccentsFreeSearchedText, textCompareFn));
        return [
            createGroupHeaderResult(groupLocKey),
            ...searchGroupResult,
        ];
    } else {
        return EMPTY_ARRAY;
    }
};

/**
 * Získá id profilů a formulářů nacházejících se v podstromu formulářů definovaným počátečním id. Nezískává id pro složky formulářů.
 * Pokud označuje počáteční id formulář, je také přidáno do výsledku.
 * @param subtreeRootId Id počátečního formuláře pro hledání.
 * @param formItems Data formulářů.
 */
export const getIdsForSubtree = (subtreeRootId: number, formItems: Forms): SubtreeIds => {
    const { type, children, profiles }: IMenuItemData = formItems[subtreeRootId];
    if (type === MenuItemType.form) {
        return {
            formItemIds: [subtreeRootId],
            profileIds: profiles ? profiles : EMPTY_ARRAY,
        };
    } else if (children) {
        return children.reduce((result, formItemId) => {
            const { formItemIds, profileIds } = getIdsForSubtree(formItemId, formItems);
            return {
                formItemIds: result.formItemIds.concat(formItemIds),
                profileIds: result.profileIds.concat(profileIds),
            };
        }, { formItemIds: [], profileIds: [] } as SubtreeIds);
    } else {
        return { formItemIds: EMPTY_ARRAY, profileIds: EMPTY_ARRAY };
    }
};
